import { defineSlotRecipe } from '@chakra-ui/react';
import { popoverAnatomy } from '@chakra-ui/react/anatomy';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/menu-popover.html
 *
 * Popovers portal outside the <body> font-size cascade in some browsers, so we
 * pin font-size + line-height on `content` explicitly. Without this pin the
 * popover renders at the UA default 16px instead of the 13px V2 base, which
 * makes embedded form controls (inputs, button groups) look oversized against
 * the rest of the app.
 *
 * Border: `border.subtle` (lighter than default) pairs better with shadow.md —
 * the previous `border.default` read as a double ring on small popovers.
 * Inline edit popovers in the real app can be wide (300-400px+), so there's no
 * default min-width here; let consumers size.
 *
 * v3 port notes:
 * - Slots unchanged from v2's `popoverAnatomy` (`content`, `header`, `body`,
 *   `footer`, `arrow`, `closeButton`→`closeTrigger` is the one rename) — v3's
 *   real anatomy (`@ark-ui/react/popover`) only has `content`/`title`/
 *   `description`/`anchor`/`trigger`/`positioner`/`arrow`/`arrowTip`/
 *   `indicator`/`closeTrigger`, but Chakra's own `popoverAnatomy` re-extends
 *   it with `header`/`body`/`footer` (confirmed in `@chakra-ui/react`'s
 *   `anatomy.js`: `popoverAnatomy$1.extendWith('header', 'body', 'footer')`)
 *   specifically so `PopoverHeader`/`PopoverBody`/`PopoverFooter` convenience
 *   components — which this app already uses — have real recipe slots to
 *   read from, same as v2.
 * - `content.width` is a flat `xs` (320px), v2's default: `extendTheme`
 *   merged this file over Chakra's own popover theme, which set
 *   `width: "xs"`, so every unsized popover on v2 was 320px wide. The
 *   built-in v3 recipe expresses the same size as a breakpoint object
 *   (`{ base: min(100dvw - 1rem, …), sm: var(--popover-size) }`). Keep it
 *   flat: v3 merges recipe and prop styles per breakpoint, so a call-site
 *   `width="500px"` would only replace the `base` step and the `sm` step
 *   would pull it back to 320px from 480px up. A plain string replaces the
 *   whole object.
 * - Same bleed-through on `footer`: the built-in makes it `display: flex`,
 *   where v2's `PopoverFooter` was a plain block. Consumers wrap their
 *   actions in their own `MFlex` (`justifyContent="space-between"`,
 *   `justify="right"`), which as a flex item shrinks to content and leaves
 *   nothing to justify. Reset to `block`; call sites that want the footer
 *   itself to be the flex row still pass `display="flex"`.
 * - The built-in's `size` variant (`defaultVariants: { size: 'md' }`, applied
 *   even though this app never sets `size` almost anywhere) only writes
 *   `--popover-padding`, which nothing in this recipe's base reads (every
 *   slot here uses literal `px`/`py`, not the built-in's css-var padding) —
 *   confirmed inert, unlike Menu's/Card's/Tabs' size-variant bleed-through,
 *   so no `size.md` neutralization entry is needed here. `sizes.sm` IS a real
 *   external API (`MPopover size="sm"` used in ~3 files) — kept as its own
 *   variant entry below, unchanged from v2.
 * - `Dialog.CloseTrigger`/`Menu`'s precedent holds here too:
 *   `Popover.CloseTrigger` renders no default icon in v3 (v2's
 *   `PopoverCloseButton` did) — `MPopoverWrapper.tsx` adds one explicitly.
 */

const baseStyle = {
  content: {
    width: 'xs', // v2's default, kept flat — see above
    // The built-in caps this at `--available-height`, the room on the side
    // zag picked. Content that grows after opening (a lookup popover's
    // spinner, then the record) was placed below while small, then capped to
    // the room left there: it squeezed into a scroll box, and zag never
    // flipped it because the capped box no longer overflowed. v2 had no cap.
    maxHeight: 'none',
    borderRadius: 'lg', // 8px
    border: '1px solid',
    borderColor: 'border.subtle',
    boxShadow: 'md',
    bg: 'bg.surface',
    fontSize: 'sm', // 13px — anchor the cascade for embedded content
    lineHeight: 'base',
    color: 'fg.primary',
    // Ensure any inherited styles are ignored (e.g. table column filter)
    textTransform: 'none',
    letterSpacing: 'normal',
    // v3 moves keyboard focus to Popover.Content (tabIndex=-1) when a
    // popover opens, which paints the browser default focus ring around
    // the entire popover. Suppress it — focus is already signaled by the
    // popover being visible, and any interactive child gets its own halo.
    _focusVisible: {
      outline: 'none',
      boxShadow: 'md',
    },
  },
  closeTrigger: {
    // `top`/`right` only place the button if it's taken out of flow — without
    // this it renders as a normal block sibling above `header`, stacking the
    // "x" on its own line instead of sitting inline with the header text.
    position: 'absolute',
    bg: 'none',
    color: 'fg.muted',
    top: 2, // 8px
    right: 4, // 16px - ensure there is space if scrollbar is present
    width: '24px',
    height: '24px',
    borderRadius: 'sm',
    // v3's preflight no longer gives buttons `cursor: pointer`.
    cursor: 'pointer',
    // Consumers pass a bare `<Icon asChild><MdClose /></Icon>`, which would
    // otherwise size to the content's 14px font — match the modal close X.
    _icon: { boxSize: 5 },
    _hover: { bg: 'bg.hover', color: 'fg.primary' },
  },
  header: {
    px: 3,
    py: 2.5,
    borderBottomWidth: 0,
    fontWeight: 600,
    fontSize: 'md',
    color: 'fg.strong',
  },
  body: {
    px: 3,
    py: 3,
    fontSize: 'sm', // 13px — prevents the "huge text" effect
    color: 'fg.primary',
  },
  footer: {
    display: 'block', // neutralize the built-in's `display: flex` — see above
    px: 3,
    py: 2,
    borderTopWidth: 0,
    fontSize: 'sm',
  },
  // Ark paints the tip from `--arrow-background`. A `bg` here painted the
  // arrow's square wrapper instead, a box behind the tip.
  arrow: {
    '--arrow-background': 'colors.bg.surface',
  },
  // The built-in gives the tip its 1px border; match the content's color.
  arrowTip: {
    borderColor: 'border.subtle',
  },
};

const sizes = {
  sm: { content: { w: '240px' } },
  smaller: { content: { w: '222px' } },
};

// Legacy variant kept for call sites using variant="popover".
const popover = {
  content: {
    p: 3,
    border: '1px solid',
    borderColor: 'border.subtle',
    boxShadow: 'md',
    fontSize: 'sm',
  },
};

const popoverTheme = defineSlotRecipe({
  // The full anatomy, in the built-in's order. The theme merge combines the
  // two `slots` arrays by index, so a shorter custom list overwrote the
  // built-in's first entries and dropped `arrowTip` (with its border),
  // `positioner`, `trigger`, `anchor` and `indicator`.
  slots: popoverAnatomy.keys(),
  base: baseStyle,
  variants: {
    size: sizes,
    variant: { popover },
  },
});

export default popoverTheme;
